import { Badge, BadgeLevel, BadgeShape, BadgeSize, ListBox, sectionedMenuStyles } from '@vizit/atoms';
import { FormSelect } from '@vizit/forms';
import { SelectItem } from '@vizit/molecules';
import { plural } from '@vizit/utils';
import { Text as AriaText, Header } from 'react-aria-components';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { BenchmarkIdentity } from './benchmark-identity';
import { type BenchmarkOption, IMAGE_TYPE_NAMES, findBenchmarkOption, getBenchmarkOptionKey } from './benchmark-option';
import { imagesNeededLabel } from './benchmark-option';
import { BenchmarkUnderMinimumIcon } from './benchmark-under-minimum-icon';

interface BenchmarkSelectFieldProps<T extends FieldValues> {
  /** The control object from the parent `useForm` hook. */
  control: Control<T>;
  /** The name of the benchmark field in the parent form; its value is a benchmark option key. */
  name: Path<T>;
  /** Every benchmark to offer, Vizit and custom, in every image type; listed in this order within their group. */
  options: BenchmarkOption[];
  /** True while the custom benchmarks are still loading. */
  isLoading?: boolean;
  /** Shown under the field when the custom benchmarks could not be loaded; the retailer group stays usable. */
  loadErrorMessage?: string;
  /** When true, the label shows a required marker. */
  isRequired?: boolean;
}

function TypeBadge({ option }: { option: BenchmarkOption }) {
  if (!option.scope) {
    return null;
  }
  return (
    <Badge level={BadgeLevel.Default} shape={BadgeShape.Box} size={BadgeSize.Sm} className="shrink-0">
      {IMAGE_TYPE_NAMES[option.scope]}
    </Badge>
  );
}

/** The trigger's reading of the pick: the logo or mosaic, the name and, for a custom set, its image type. */
function SelectedBenchmark({ option }: { option: BenchmarkOption }) {
  return (
    <span className="flex min-w-0 items-center gap-4">
      <BenchmarkIdentity option={option} className="h-10 w-10" />
      <span className="truncate">{option.name}</span>
      <TypeBadge option={option} />
    </span>
  );
}

/**
 * The benchmark a new collection is scored against: a select grouped into the retailer benchmarks (Amazon, Walmart,
 * with their logos) and the workspace's own sets of every image type. A set's row shows its mosaic, how many
 * reference images it has and its image type; a set under its minimum is listed disabled with a warning whose
 * tooltip says how many images it still needs. The caller locks the collection's image type to a picked set's.
 */
function BenchmarkSelectField<T extends FieldValues>(props: BenchmarkSelectFieldProps<T>) {
  const { control, name, options, isLoading = false, loadErrorMessage, isRequired = false } = props;

  const retailerOptions = options.filter((option) => option.kind === 'vizit');
  const customOptions = options.filter((option) => option.kind === 'custom');
  const disabledKeys = options.filter((option) => option.disabled).map(getBenchmarkOptionKey);

  const renderValue = (key: unknown) => {
    const option = findBenchmarkOption(options, typeof key === 'string' ? key : undefined);
    return option ? <SelectedBenchmark option={option} /> : null;
  };

  return (
    <FormSelect
      control={control}
      name={name}
      label="Benchmark"
      placeholder="Choose benchmark"
      isRequired={isRequired}
      helperText={loadErrorMessage}
      disabledKeys={disabledKeys}
      renderValue={renderValue}
      reserveMessageSpace={false}
      data-testid="collection-benchmark"
    >
      <ListBox.Section id="vizit">
        <Header className={sectionedMenuStyles.header()}>RETAILER BENCHMARKS</Header>
        {retailerOptions.map((option) => (
          <SelectItem key={getBenchmarkOptionKey(option)} id={getBenchmarkOptionKey(option)} textValue={option.name}>
            <span className="flex items-center gap-4">
              <BenchmarkIdentity option={option} className="h-10 w-10 justify-center" />
              <span className="flex min-w-0 flex-col">
                <AriaText slot="label" className="group-data-[disabled]:text-disabled">
                  {option.name}
                </AriaText>
                {/* Shown, not left to a tooltip, and read as the option's description. */}
                {option.disabled && option.disabledReason && (
                  <AriaText slot="description" className="text-tertiary text-xs">
                    {option.disabledReason}
                  </AriaText>
                )}
              </span>
            </span>
          </SelectItem>
        ))}
      </ListBox.Section>
      {(customOptions.length > 0 || isLoading) && (
        <ListBox.Section id="custom" className={sectionedMenuStyles.section({ withDivider: true })}>
          <Header className={sectionedMenuStyles.header()}>YOUR BENCHMARKS</Header>
          {customOptions.map((option) => (
            <SelectItem
              key={getBenchmarkOptionKey(option)}
              id={getBenchmarkOptionKey(option)}
              textValue={option.name}
              // The row fills the item so the badge sits at its end; a disabled set still answers the pointer, so its
              // warning can show why it cannot be picked.
              className="pr-14 data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed [&>div]:w-full"
            >
              <span className="flex w-full items-center gap-6">
                <BenchmarkIdentity option={option} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <AriaText slot="label" className="truncate group-data-[disabled]:text-disabled">
                    {option.name}
                  </AriaText>
                  {/* The count is shown; why a disabled set cannot be picked is only read, as the warning shows it. */}
                  <AriaText slot="description" className="text-tertiary text-xs">
                    {plural(option.imageCount ?? 0, 'reference image')}
                    {option.disabled && <span className="sr-only">, {imagesNeededLabel(option)}</span>}
                  </AriaText>
                </span>
                <TypeBadge option={option} />
                {option.disabled && <BenchmarkUnderMinimumIcon option={option} showTooltip />}
              </span>
            </SelectItem>
          ))}
          {isLoading && customOptions.length === 0 && (
            <SelectItem id="loading" textValue="Loading benchmarks" isDisabled>
              Loading benchmarks…
            </SelectItem>
          )}
        </ListBox.Section>
      )}
    </FormSelect>
  );
}

export { BenchmarkSelectField };
export type { BenchmarkSelectFieldProps };
